import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconMapPin, IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const WarehouseLocations = () => {
    const [appId, setAppId] = useState(null);
    const [locations, setLocations] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [form, setForm] = useState({ warehouse: '', code: '', description: '' });

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadLocations();
        loadWarehouses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadLocations = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouse-locations/`);
            setLocations(res.data || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar localizações' });
        }
    };

    const loadWarehouses = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            setWarehouses(res.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCreate = () => {
        setEditingLocation(null);
        setForm({ warehouse: '', code: '', description: '' });
        setDialogOpen(true);
    };

    const handleOpenEdit = (location) => {
        setEditingLocation(location);
        setForm({ warehouse: location.warehouse, code: location.code, description: location.description || '' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingLocation) {
                await api.patch(`v1/apps/${appId}/inventory/warehouse-locations/${editingLocation.id}/`, form);
            } else {
                await api.post(`v1/apps/${appId}/inventory/warehouse-locations/`, form);
            }
            setDialogOpen(false);
            loadLocations();
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || Object.values(error.response?.data || {})[0] || 'Erro ao salvar localização'
            });
        }
    };

    const handleDelete = async (location) => {
        if (!window.confirm(`Excluir a localização "${location.code}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/inventory/warehouse-locations/${location.id}/`);
            setLocations((prev) => prev.filter((l) => l.id !== location.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir localização' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconMapPin}
                gradient="teal"
                title="Localizações"
                subtitle="Corredores/prateleiras dentro de cada depósito"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Nova localização
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Depósito</TableCell>
                                <TableCell>Código</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {locations.map((location) => (
                                <TableRow key={location.id} hover>
                                    <TableCell>{location.warehouse_name}</TableCell>
                                    <TableCell>{location.code}</TableCell>
                                    <TableCell>{location.description || '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(location)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(location)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {locations.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Nenhuma localização cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingLocation ? 'Editar localização' : 'Nova localização'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            fullWidth
                            label="Depósito"
                            value={form.warehouse}
                            onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
                        >
                            {warehouses.map((warehouse) => (
                                <MenuItem key={warehouse.id} value={warehouse.id}>
                                    {warehouse.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Código (ex: A1-03)"
                            fullWidth
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                        />
                        <TextField
                            label="Descrição (opcional)"
                            fullWidth
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WarehouseLocations;
